import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, Check } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NAV_ITEMS = [
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

/**
 * Navbar Component - EXACT ORIGINAL DESIGN
 */
export default function Navbar({ 
  selectedLanguage, 
  setSelectedLanguage, 
  languages,
  mobileMenuOpen,
  setMobileMenuOpen 
}) {
  const scrollToSection = (id) => {
    const el = document.getElementById(id.replace('#', ''));
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/logo.png" 
                alt="YASNO" 
                className="w-10 h-10 rounded-xl object-contain"
              />
              <span className="text-xl font-bold tracking-tight hidden sm:block">
                <span className="text-white">YAS</span>
                <span className="text-primary">NO</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Language Selector - Prominent Button - Similar style to Start Free Trial */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm" 
                    className="rounded-full bg-primary/20 border-2 border-primary hover:bg-primary/30 gap-2 px-4 py-2 neon-glow" 
                    data-testid="language-selector"
                  >
                    <Globe className="w-4 h-4 text-primary" />
                    <span className="text-sm font-bold text-white">
                      {languages.find(l => l.code === selectedLanguage)?.flag} {languages.find(l => l.code === selectedLanguage)?.name}
                    </span>
                    <ChevronDown className="w-3 h-3 text-primary" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto bg-background/95 backdrop-blur-md border-primary/30">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => {
                        setSelectedLanguage(lang.code);
                        localStorage.setItem('yasno_language', lang.code);
                      }}
                      className={`flex items-center gap-2 cursor-pointer ${
                        selectedLanguage === lang.code ? 'bg-primary/20 text-primary font-bold' : ''
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {selectedLanguage === lang.code && <Check className="w-4 h-4 ml-auto text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/login">
                <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 neon-glow" data-testid="get-started-btn">
                  Start Free Trial
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={{ height: mobileMenuOpen ? 'auto' : 0, opacity: mobileMenuOpen ? 1 : 0 }}
        className="fixed top-16 left-0 right-0 z-40 glass border-b border-white/5 md:hidden overflow-hidden"
      >
        <div className="px-4 py-4 space-y-2">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.label}
              onClick={() => scrollToSection(item.href)}
              className="block w-full text-left px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
            >
              {item.label}
            </button>
          ))}
          <div className="pt-2 border-t border-white/10">
            <Link to="/login" className="block w-full">
              <Button variant="ghost" className="w-full justify-start">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </>
  );
}
